import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  eachMonthOfInterval,
  parseISO,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import {
  Users,
  DollarSign,
  MessageSquare,
  HeadphonesIcon,
  TrendingUp,
  Search,
  LogOut,
  LayoutDashboard,
  CreditCard,
  ChevronRight,
  AlertTriangle,
  CalendarDays,
  Filter,
  BarChart3,
  RefreshCw,
  Globe2,
  MousePointerClick,
  MapPin,
  Smartphone,
  Route,
  Clock3,
  Database,
  FileText,
  Ban,
  RotateCcw,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings2,
  MonitorCog,
  X
} from 'lucide-react';

const BrandIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5 4h14v4.2L8.7 20H5.2l9.1-10.4H5V4z" />
  </svg>
);

const getElapsedLabel = (dateStr) => {
  if (!dateStr) return '-';
  const start = new Date(dateStr);
  if (Number.isNaN(start.getTime())) return '-';
  const now = new Date();
  const diffMs = Math.max(now - start, 0);
  const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const months = Math.floor(totalDays / 30);
  const days = totalDays % 30;

  if (months > 0) {
    return `${months}m ${days}d`;
  }
  return `${totalDays}d`;
};

// Simple Bar Chart Component
const SimpleBarChart = ({ data, title }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-zinc-700">{title}</h4>
      <div className="space-y-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-3">
            <span className="text-xs text-zinc-500 w-16 text-right">{item.label}</span>
            <div className="flex-1 h-6 bg-zinc-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                style={{ width: `${(item.value / maxValue) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-zinc-700 w-8">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AnalyticsKpiCard = ({ icon: Icon, label, value, helper, tone = 'sky' }) => {
  const styles = {
    sky: 'bg-sky-100 text-sky-600',
    violet: 'bg-violet-100 text-violet-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    rose: 'bg-rose-100 text-rose-600',
  };

  return (
    <Card className="border-zinc-200">
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${styles[tone] || styles.sky}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-zinc-900">{value}</p>
            <p className="text-xs text-zinc-500">{label}</p>
            {helper ? <p className="text-[11px] text-zinc-400 mt-1">{helper}</p> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const HealthBadge = ({ status }) => {
  const styles = {
    healthy: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-700',
    attention: 'bg-red-100 text-red-700',
  };
  const labels = {
    healthy: 'Saudável',
    warning: 'Atenção',
    attention: 'Crítico',
  };

  return (
    <Badge className={`${styles[status] || styles.healthy} border-0`}>
      {labels[status] || 'Saudável'}
    </Badge>
  );
};

const ADMIN_TABS = [
  { id: 'overview', label: 'Visão Geral', icon: LayoutDashboard },
  { id: 'users', label: 'Usuários', icon: Users },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'payments', label: 'Pagamentos', icon: CreditCard },
  { id: 'billing-requests', label: 'Cancelamentos', icon: Ban },
  { id: 'support', label: 'Suporte', icon: HeadphonesIcon },
  { id: 'settings', label: 'Configurações', icon: Settings2 },
];

const SimpleGeoMap = ({ locations = [] }) => {
  const validLocations = locations.filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lon));
  const maxCount = Math.max(...locations.map((item) => item.count || 0), 1);

  const projectPoint = (lat, lon) => {
    const x = ((lon + 180) / 360) * 1000;
    const y = ((90 - lat) / 180) * 500;
    return { x, y };
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-gradient-to-br from-sky-50 via-white to-violet-50 p-4">
      <svg viewBox="0 0 1000 500" className="w-full h-[280px] rounded-xl bg-[radial-gradient(circle_at_top,#ffffff,transparent_60%)]">
        <rect x="0" y="0" width="1000" height="500" rx="24" fill="#F8FAFC" />
        {[100, 200, 300, 400].map((y) => (
          <line key={`y-${y}`} x1="0" y1={y} x2="1000" y2={y} stroke="#E2E8F0" strokeDasharray="6 8" />
        ))}
        {[200, 400, 600, 800].map((x) => (
          <line key={`x-${x}`} x1={x} y1="0" x2={x} y2="500" stroke="#E2E8F0" strokeDasharray="6 8" />
        ))}
        {validLocations.map((location, index) => {
          const { x, y } = projectPoint(location.lat, location.lon);
          const radius = 7 + ((location.count || 0) / maxCount) * 14;
          return (
            <g key={`${location.country}-${location.city}-${index}`}>
              <circle cx={x} cy={y} r={radius} fill="rgba(14,165,233,0.18)" />
              <circle cx={x} cy={y} r={Math.max(radius / 2.5, 4)} fill="#7C3AED" />
            </g>
          );
        })}
        {!validLocations.length ? (
          <text x="500" y="250" textAnchor="middle" fill="#64748B" fontSize="18">
            Sem coordenadas suficientes para desenhar o mapa neste período
          </text>
        ) : null}
      </svg>
    </div>
  );
};

const RegistrationsChart = ({ data = [], view = 'day' }) => {
  const isLineView = view === 'day';

  if (!data.length) {
    return (
      <div className="h-[280px] rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 flex items-center justify-center text-sm text-zinc-500">
        Sem cadastros no período selecionado.
      </div>
    );
  }

  return (
    <div className="h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        {isLineView ? (
          <LineChart data={data} margin={{ top: 12, right: 12, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
            <XAxis dataKey="label" tick={{ fill: '#71717A', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fill: '#71717A', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip
              formatter={(value, name) => [
                `${value} ${name === 'cancellations' ? `cancelamento${value === 1 ? '' : 's'}` : `cadastro${value === 1 ? '' : 's'}`}`,
                name === 'cancellations' ? 'Cancelamentos' : 'Cadastros',
              ]}
              labelFormatter={(label) => `Período: ${label}`}
              contentStyle={{ borderRadius: 16, borderColor: '#E4E4E7' }}
            />
            <Line
              type="natural"
              dataKey="value"
              stroke="#2563EB"
              strokeWidth={2}
              dot={{ r: 2.5, strokeWidth: 1.5, fill: '#FFFFFF' }}
              activeDot={{ r: 4 }}
              name="value"
            />
            <Line
              type="natural"
              dataKey="cancellations"
              stroke="#F97316"
              strokeWidth={1.8}
              dot={{ r: 2.2, strokeWidth: 1.2, fill: '#FFFFFF' }}
              activeDot={{ r: 4 }}
              name="cancellations"
            />
          </LineChart>
        ) : (
          <BarChart data={data} margin={{ top: 12, right: 12, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
            <XAxis dataKey="label" tick={{ fill: '#71717A', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fill: '#71717A', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip
              formatter={(value, name) => [
                `${value} ${name === 'cancellations' ? `cancelamento${value === 1 ? '' : 's'}` : `cadastro${value === 1 ? '' : 's'}`}`,
                name === 'cancellations' ? 'Cancelamentos' : 'Cadastros',
              ]}
              labelFormatter={(label) => `Período: ${label}`}
              contentStyle={{ borderRadius: 16, borderColor: '#E4E4E7' }}
            />
            <Bar dataKey="value" fill="#10B981" radius={[8, 8, 0, 0]} name="value" />
            <Bar dataKey="cancellations" fill="#F97316" radius={[8, 8, 0, 0]} name="cancellations" />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const PaymentComparisonChart = ({ data = [], view = 'month' }) => {
  const lineMode = view === 'day';

  if (!data.length) {
    return (
      <div className="h-[300px] rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 flex items-center justify-center text-sm text-zinc-500">
        Sem dados financeiros no período selecionado.
      </div>
    );
  }

  return (
    <div className="h-[300px]">
      <div className="mb-3 flex flex-wrap items-center gap-4 px-1">
        <div className="flex items-center gap-2 text-xs text-zinc-600">
          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-blue-600" />
          Receita recebida
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-600">
          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          Receita prevista
        </div>
      </div>
      <ResponsiveContainer width="100%" height="100%">
        {lineMode ? (
          <LineChart data={data} margin={{ top: 12, right: 12, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
            <XAxis dataKey="label" tick={{ fill: '#71717A', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#71717A', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip
              formatter={(value) => [`R$ ${Number(value || 0).toFixed(2).replace('.', ',')}`, '']}
              labelFormatter={(label) => `Período: ${label}`}
              contentStyle={{ borderRadius: 16, borderColor: '#E4E4E7' }}
            />
            <Line type="natural" dataKey="received" name="Recebido" stroke="#2563EB" strokeWidth={2} dot={{ r: 2.5, strokeWidth: 1.5, fill: '#FFFFFF' }} activeDot={{ r: 4 }} />
            <Line type="natural" dataKey="forecast" name="Previsível" stroke="#10B981" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2.5, strokeWidth: 1.5, fill: '#FFFFFF' }} activeDot={{ r: 4 }} />
          </LineChart>
        ) : (
          <BarChart data={data} margin={{ top: 12, right: 12, left: -8, bottom: 0 }} barGap={10}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
            <XAxis dataKey="label" tick={{ fill: '#71717A', fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#71717A', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip
              formatter={(value) => [`R$ ${Number(value || 0).toFixed(2).replace('.', ',')}`, '']}
              labelFormatter={(label) => `Período: ${label}`}
              contentStyle={{ borderRadius: 16, borderColor: '#E4E4E7' }}
            />
            <Bar dataKey="received" name="Recebido" fill="#2563EB" radius={[8, 8, 0, 0]} />
            <Bar dataKey="forecast" name="Previsível" fill="#10B981" radius={[8, 8, 0, 0]} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const PIE_COLORS = ['#2563EB', '#10B981', '#F59E0B', '#7C3AED', '#F43F5E'];

const PaymentDistributionPie = ({ data = [] }) => {
  const filtered = data.filter((item) => Number(item.value) > 0);

  if (!filtered.length) {
    return (
      <div className="h-[260px] rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 flex items-center justify-center text-sm text-zinc-500">
        Sem distribuição suficiente neste período.
      </div>
    );
  }

  return (
    <div className="h-[260px]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Tooltip
            formatter={(value) => [`R$ ${Number(value || 0).toFixed(2).replace('.', ',')}`, 'Receita']}
            contentStyle={{ borderRadius: 16, borderColor: '#E4E4E7' }}
          />
          <Pie
            data={filtered}
            dataKey="value"
            nameKey="label"
            innerRadius={56}
            outerRadius={86}
            paddingAngle={3}
          >
            {filtered.map((entry, index) => (
              <Cell key={`${entry.label}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [supportStats, setSupportStats] = useState(null);
  const [landingAnalytics, setLandingAnalytics] = useState(null);
  const [paymentsData, setPaymentsData] = useState(null);
  const [billingRequests, setBillingRequests] = useState([]);
  const [billingRequestsStats, setBillingRequestsStats] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [billingRequestsLoading, setBillingRequestsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [overviewSegment, setOverviewSegment] = useState('all');
  const [usersSegmentFilter, setUsersSegmentFilter] = useState('all');
  const [cancellationAlertOpen, setCancellationAlertOpen] = useState(false);
  const [hasSeenCancellationAlert, setHasSeenCancellationAlert] = useState(false);
  const [selectedBillingRequest, setSelectedBillingRequest] = useState(null);
  const [billingRequestNotes, setBillingRequestNotes] = useState('');
  const [draggingBillingRequestId, setDraggingBillingRequestId] = useState(null);
  const [dragOverBillingColumn, setDragOverBillingColumn] = useState(null);
  
  // Filters
  const [overviewDateRange, setOverviewDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [registrationsView, setRegistrationsView] = useState('day');
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [analyticsDeviceFilter, setAnalyticsDeviceFilter] = useState('all');
  const [analyticsVariantFilter, setAnalyticsVariantFilter] = useState('all');
  const [paymentsSegmentFilter, setPaymentsSegmentFilter] = useState('all');
  const [paymentsDistributionMode, setPaymentsDistributionMode] = useState('received');
  const [billingRequestsSegmentFilter, setBillingRequestsSegmentFilter] = useState('all');
  const [supportSegmentFilter, setSupportSegmentFilter] = useState('all');
  const [supportSegmentStats, setSupportSegmentStats] = useState(null);
  const [paymentsDateRange, setPaymentsDateRange] = useState({
    from: startOfMonth(new Date()),
    to: new Date()
  });
  const [paymentsView, setPaymentsView] = useState('month');
  const [paymentsUseDateFilter, setPaymentsUseDateFilter] = useState(true);
  const [adminSidebarOpen, setAdminSidebarOpen] = useState(false);
  const [adminSidebarCollapsed, setAdminSidebarCollapsed] = useState(false);

  const handlePaymentsFromDateSelect = (date) => {
    if (!date) return;
    setPaymentsDateRange((prev) => {
      const nextTo = prev?.to && prev.to < date ? date : (prev?.to || date);
      return {
        from: date,
        to: nextTo,
      };
    });
  };

  const handlePaymentsToDateSelect = (date) => {
    if (!date) return;
    setPaymentsDateRange((prev) => {
      const nextFrom = prev?.from && prev.from > date ? date : (prev?.from || date);
      return {
        from: nextFrom,
        to: date,
      };
    });
  };

  const fetchData = useCallback(async () => {
    try {
      const usersParams = new URLSearchParams({ limit: '10' });
      if (usersSegmentFilter !== 'all') {
        usersParams.set('account_segment', usersSegmentFilter);
      }
      const allUsersParams = new URLSearchParams({ limit: '1000' });
      if (usersSegmentFilter !== 'all') {
        allUsersParams.set('account_segment', usersSegmentFilter);
      }

      const [statsRes, usersRes, supportRes, allUsersRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: 'include' }),
        fetch(`${API}/admin/users?${usersParams.toString()}`, { credentials: 'include' }),
        fetch(`${API}/admin/support/stats`, { credentials: 'include' }),
        fetch(`${API}/admin/users?${allUsersParams.toString()}`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
        if ((statsData?.billing_requests?.pending || 0) > 0 && !hasSeenCancellationAlert) {
          setCancellationAlertOpen(true);
        }
      }
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users);
      }
      if (supportRes.ok) setSupportStats(await supportRes.json());
      if (allUsersRes.ok) {
        const data = await allUsersRes.json();
        setAllUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [hasSeenCancellationAlert, usersSegmentFilter]);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/app', { replace: true });
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const fetchLandingAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: format(dateRange.from, 'yyyy-MM-dd'),
        end_date: format(dateRange.to, 'yyyy-MM-dd'),
      });
      if (analyticsDeviceFilter !== 'all') {
        params.set('device_type', analyticsDeviceFilter);
      }
      if (analyticsVariantFilter !== 'all') {
        params.set('landing_variant', analyticsVariantFilter);
      }

      const response = await fetch(`${API}/admin/analytics/landing?${params.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('analytics_failed');
      }
      setLandingAnalytics(await response.json());
    } catch (error) {
      console.error('Error fetching landing analytics:', error);
      toast.error('Erro ao carregar analytics da página principal');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [dateRange, analyticsDeviceFilter, analyticsVariantFilter]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchLandingAnalytics();
    }
  }, [activeTab, fetchLandingAnalytics]);

  const fetchPaymentsDashboard = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const fromDate = paymentsDateRange?.from || startOfMonth(new Date());
      const toDate = paymentsDateRange?.to || paymentsDateRange?.from || new Date();
      const params = new URLSearchParams({ view: paymentsView });
      if (paymentsUseDateFilter) {
        params.set('start_date', format(fromDate, 'yyyy-MM-dd'));
        params.set('end_date', format(toDate, 'yyyy-MM-dd'));
      }
      if (paymentsSegmentFilter !== 'all') {
        params.set('account_segment', paymentsSegmentFilter);
      }
      const response = await fetch(`${API}/admin/payments?${params.toString()}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('payments_failed');
      }
      setPaymentsData(await response.json());
    } catch (error) {
      console.error('Error fetching payments dashboard:', error);
      toast.error('Erro ao carregar visão de pagamentos');
    } finally {
      setPaymentsLoading(false);
    }
  }, [paymentsDateRange, paymentsView, paymentsUseDateFilter, paymentsSegmentFilter]);

  useEffect(() => {
    if (activeTab === 'payments') {
      fetchPaymentsDashboard();
    }
  }, [activeTab, fetchPaymentsDashboard]);

  useEffect(() => {
    const handleResizeObserverError = (event) => {
      const message = event?.message || '';
      if (message.includes('ResizeObserver loop completed with undelivered notifications')) {
        event.stopImmediatePropagation?.();
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    return () => window.removeEventListener('error', handleResizeObserverError);
  }, []);

  const paymentsReceivedLabel = paymentsUseDateFilter ? 'Receita recebida no período' : 'Receita recebida no mês atual';
  const paymentsReceivedValue = paymentsUseDateFilter
    ? (paymentsData?.dashboard?.kpis?.range_received || 0)
    : (paymentsData?.dashboard?.kpis?.current_month_received || 0);
  const paymentsReceivedHelper = paymentsUseDateFilter
    ? 'Cobranças confirmadas no recorte selecionado'
    : 'Cobranças confirmadas no ciclo atual';
  const paymentsForecastLabel = paymentsUseDateFilter ? 'Receita prevista no período' : 'Receita previsível em 30 dias';
  const paymentsForecastValue = paymentsUseDateFilter
    ? (paymentsData?.dashboard?.kpis?.range_forecast || 0)
    : (paymentsData?.dashboard?.kpis?.upcoming_30_days_forecast || 0);
  const paymentsForecastHelper = paymentsUseDateFilter
    ? 'Cobranças previstas no recorte selecionado'
    : 'Próximas cobranças previstas';

  const fetchBillingRequests = useCallback(async () => {
    setBillingRequestsLoading(true);
    try {
      const params = new URLSearchParams();
      if (billingRequestsSegmentFilter !== 'all') {
        params.set('account_segment', billingRequestsSegmentFilter);
      }
      const [requestsRes, statsRes] = await Promise.all([
        fetch(`${API}/admin/billing/requests?${params.toString()}`, { credentials: 'include' }),
        fetch(`${API}/admin/billing/requests/stats?${params.toString()}`, { credentials: 'include' }),
      ]);
      if (requestsRes.ok) {
        const data = await requestsRes.json();
        setBillingRequests(data.requests || []);
      }
      if (statsRes.ok) {
        setBillingRequestsStats(await statsRes.json());
      }
    } catch (error) {
      console.error('Error fetching billing requests:', error);
      toast.error('Erro ao carregar cancelamentos');
    } finally {
      setBillingRequestsLoading(false);
    }
  }, [billingRequestsSegmentFilter]);

  const fetchSupportSegmentStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (supportSegmentFilter !== 'all') {
        params.set('account_segment', supportSegmentFilter);
      }
      const response = await fetch(`${API}/admin/support/stats?${params.toString()}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('support_stats_failed');
      }
      setSupportSegmentStats(await response.json());
    } catch (error) {
      console.error('Error fetching support segment stats:', error);
      toast.error('Erro ao carregar suporte por segmento');
    }
  }, [supportSegmentFilter]);

  useEffect(() => {
    if (activeTab === 'billing-requests') {
      fetchBillingRequests();
    }
  }, [activeTab, fetchBillingRequests]);

  useEffect(() => {
    if (activeTab === 'support') {
      fetchSupportSegmentStats();
    }
  }, [activeTab, fetchSupportSegmentStats]);

  useEffect(() => {
    if (!user || user.role !== 'admin') return undefined;
    const interval = window.setInterval(() => {
      fetchData();
      if (activeTab === 'billing-requests') {
        fetchBillingRequests();
      }
    }, 60000);
    return () => window.clearInterval(interval);
  }, [user, activeTab, fetchData, fetchBillingRequests]);

  useEffect(() => {
    if (activeTab !== 'users') return;
    if (searchTerm.trim()) {
      searchUsers();
      return;
    }
    fetchData();
  }, [usersSegmentFilter]);

  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      fetchData();
      return;
    }

    try {
      const params = new URLSearchParams({ search: searchTerm });
      if (usersSegmentFilter !== 'all') {
        params.set('account_segment', usersSegmentFilter);
      }
      const response = await fetch(`${API}/admin/users?${params.toString()}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      toast.error('Erro na busca');
    }
  };

  const getUserSegment = (userItem) => (
    userItem?.account_segment === 'business' ? 'business' : 'personal'
  );

  const getOverviewStatsForSegment = () => {
    if (overviewSegment === 'business') {
      return {
        totalUsers: stats?.users?.by_segment?.business?.total || 0,
        mrr: stats?.revenue?.by_segment?.business || 0,
        byPlan: stats?.users?.by_segment?.business?.by_plan || {},
      };
    }

    if (overviewSegment === 'personal') {
      return {
        totalUsers: stats?.users?.by_segment?.personal?.total || 0,
        mrr: stats?.revenue?.by_segment?.personal || 0,
        byPlan: stats?.users?.by_segment?.personal?.by_plan || {},
      };
    }

    return {
      totalUsers: stats?.users?.total || 0,
      mrr: stats?.revenue?.mrr || 0,
      byPlan: stats?.users?.by_plan || {},
    };
  };

  const getOverviewUsersInRange = () => {
    const start = startOfDay(overviewDateRange.from);
    const end = endOfDay(overviewDateRange.to);

    return allUsers.filter((u) => {
      if (!u.created_at) return false;
      const createdAt = parseISO(u.created_at);
      if (Number.isNaN(createdAt.getTime())) return false;
      const matchesDate = createdAt >= start && createdAt <= end;
      if (!matchesDate) return false;
      if (overviewSegment === 'all') return true;
      return getUserSegment(u) === overviewSegment;
    });
  };

  const getOverviewCancellationEventsInRange = () => {
    const start = startOfDay(overviewDateRange.from);
    const end = endOfDay(overviewDateRange.to);

    return allUsers.filter((u) => {
      if (!u.cancellation_requested_at) return false;
      const cancellationAt = parseISO(u.cancellation_requested_at);
      if (Number.isNaN(cancellationAt.getTime())) return false;
      const matchesDate = cancellationAt >= start && cancellationAt <= end;
      if (!matchesDate) return false;
      if (overviewSegment === 'all') return true;
      return getUserSegment(u) === overviewSegment;
    });
  };

  const getOverviewActiveUsers = () => (
    allUsers.filter((u) => {
      if (overviewSegment !== 'all' && getUserSegment(u) !== overviewSegment) return false;
      return ['active', 'trialing'].includes(u.plan_status) && !u.cancellation_requested_at;
    })
  );

  const getOverviewPlanPerformance = () => {
    const sales = { starter: 0, pro: 0, business: 0 };
    const cancellations = { starter: 0, pro: 0, business: 0 };
    const actives = { starter: 0, pro: 0, business: 0 };

    getOverviewUsersInRange().forEach((u) => {
      if (sales[u.plan] !== undefined) sales[u.plan] += 1;
    });

    getOverviewCancellationEventsInRange().forEach((u) => {
      if (cancellations[u.plan] !== undefined) cancellations[u.plan] += 1;
    });

    getOverviewActiveUsers().forEach((u) => {
      if (actives[u.plan] !== undefined) actives[u.plan] += 1;
    });

    return { sales, cancellations, actives };
  };

  const getRegistrationsByPeriod = () => {
    const filteredUsers = getOverviewUsersInRange();
    const filteredCancellations = getOverviewCancellationEventsInRange();

    if (registrationsView === 'day') {
      const days = eachDayOfInterval({ start: overviewDateRange.from, end: overviewDateRange.to });
      return days.map((day) => {
        const dayKey = format(day, 'yyyy-MM-dd');
        const count = filteredUsers.filter((u) => format(parseISO(u.created_at), 'yyyy-MM-dd') === dayKey).length;
        const cancellations = filteredCancellations.filter((u) => format(parseISO(u.cancellation_requested_at), 'yyyy-MM-dd') === dayKey).length;
        return {
          label: format(day, 'dd/MM'),
          value: count,
          cancellations,
        };
      });
    }

    if (registrationsView === 'month') {
      const months = eachMonthOfInterval({ start: overviewDateRange.from, end: overviewDateRange.to });
      return months.map((month) => {
        const monthKey = format(month, 'yyyy-MM');
        const count = filteredUsers.filter((u) => format(parseISO(u.created_at), 'yyyy-MM') === monthKey).length;
        const cancellations = filteredCancellations.filter((u) => format(parseISO(u.cancellation_requested_at), 'yyyy-MM') === monthKey).length;
        return {
          label: format(month, 'MMM/yy', { locale: ptBR }),
          value: count,
          cancellations,
        };
      });
    }

    const startYear = overviewDateRange.from.getFullYear();
    const endYear = overviewDateRange.to.getFullYear();
    return Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index).map((year) => {
      const count = filteredUsers.filter((u) => parseISO(u.created_at).getFullYear() === year).length;
      const cancellations = filteredCancellations.filter((u) => parseISO(u.cancellation_requested_at).getFullYear() === year).length;
      return {
        label: String(year),
        value: count,
        cancellations,
      };
    });
  };

  const getPlanBadge = (plan) => {
    const styles = {
      starter: 'bg-zinc-100 text-zinc-700',
      pro: 'bg-emerald-100 text-emerald-700',
      business: 'bg-amber-100 text-amber-700'
    };
    return <Badge className={`${styles[plan]} border-0`}>{plan}</Badge>;
  };

  const updateBillingRequestStatus = async (requestId, status, adminNotes = '') => {
    try {
      const response = await fetch(`${API}/admin/billing/requests/${requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, admin_notes: adminNotes }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Erro ao atualizar solicitação');
      }
      toast.success('Solicitação atualizada');
      setSelectedBillingRequest(null);
      setBillingRequestNotes('');
      fetchBillingRequests();
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Erro ao atualizar solicitação');
    }
  };

  const handleBillingDragStart = (requestId) => {
    setDraggingBillingRequestId(requestId);
  };

  const handleBillingDragEnd = () => {
    setDraggingBillingRequestId(null);
    setDragOverBillingColumn(null);
  };

  const handleBillingDrop = async (status) => {
    if (!draggingBillingRequestId) return;
    const requestItem = billingRequests.find((item) => item.request_id === draggingBillingRequestId);
    handleBillingDragEnd();
    if (!requestItem || requestItem.status === status) return;
    await updateBillingRequestStatus(requestItem.request_id, status);
  };

  const getBillingRequestStatusBadge = (status) => {
    const styles = {
      requested: 'bg-orange-100 text-orange-700',
      in_progress: 'bg-blue-100 text-blue-700',
      payment_cancelled: 'bg-amber-100 text-amber-700',
      completed: 'bg-emerald-100 text-emerald-700',
      dismissed: 'bg-zinc-100 text-zinc-700',
    };
    const labels = {
      requested: 'Solicitado',
      in_progress: 'Em tratamento',
      payment_cancelled: 'Pagamento cancelado',
      completed: 'Concluído',
      dismissed: 'Encerrado',
    };
    return <Badge className={`${styles[status] || styles.requested} border-0`}>{labels[status] || status}</Badge>;
  };

  const getBillingRequestTypeBadge = (requestType) => {
    const styles = {
      cancellation: 'bg-red-100 text-red-700',
      reactivation: 'bg-sky-100 text-sky-700',
    };
    const labels = {
      cancellation: 'Cancelamento',
      reactivation: 'Reativação',
    };
    return <Badge className={`${styles[requestType] || 'bg-zinc-100 text-zinc-700'} border-0`}>{labels[requestType] || requestType}</Badge>;
  };

  const getBillingRequestTimeInfo = (requestItem) => {
    const created = requestItem?.created_at ? new Date(requestItem.created_at) : null;
    const slaDue = requestItem?.sla_due_at ? new Date(requestItem.sla_due_at) : null;
    if (!created || !slaDue || Number.isNaN(created.getTime()) || Number.isNaN(slaDue.getTime())) {
      return null;
    }
    const now = new Date();
    const remainingMs = slaDue - now;
    const elapsedMs = now - created;
    const elapsedMinutes = Math.floor(elapsedMs / (1000 * 60));
    const hours = Math.floor(Math.abs(remainingMs) / (1000 * 60 * 60));
    const minutes = Math.floor((Math.abs(remainingMs) % (1000 * 60 * 60)) / (1000 * 60));
    const breached = remainingMs < 0 && !['completed', 'dismissed'].includes(requestItem.status);
    return {
      elapsedMinutes,
      label: ['completed', 'dismissed'].includes(requestItem.status)
        ? `Concluído em ${Math.floor(elapsedMinutes / 60)}h ${elapsedMinutes % 60}m`
        : breached
          ? `SLA estourado há ${hours}h ${minutes}m`
          : `Restam ${hours}h ${minutes}m`,
      tone: ['completed', 'dismissed'].includes(requestItem.status)
        ? 'bg-zinc-100 text-zinc-700'
        : breached
          ? 'bg-red-100 text-red-700'
          : remainingMs < 15 * 60 * 1000
            ? 'bg-orange-100 text-orange-700'
            : 'bg-emerald-100 text-emerald-700',
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const overviewStats = getOverviewStatsForSegment();
  const overviewUsersInRange = getOverviewUsersInRange();
  const overviewCancellationsInRange = getOverviewCancellationEventsInRange();
  const overviewActiveUsers = getOverviewActiveUsers();
  const overviewPlanPerformance = getOverviewPlanPerformance();
  const currentSupportStats = activeTab === 'support' ? (supportSegmentStats || supportStats) : supportStats;
  const sidebarBadgeMap = {
    'billing-requests': stats?.billing_requests?.pending || 0,
    support: supportStats?.by_status?.open || 0,
  };
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setAdminSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-50">
        <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setAdminSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-violet-600 flex items-center justify-center">
                <BrandIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <span className="font-heading text-lg font-semibold text-zinc-900">Zapt Resumo</span>
                <p className="text-[11px] uppercase tracking-[0.22em] text-zinc-400">Admin</p>
              </div>
            </div>
            <Badge className="bg-red-100 text-red-700 border-0">Admin</Badge>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <Button
              variant="outline"
              size="icon"
              className="hidden md:inline-flex"
              onClick={() => setAdminSidebarCollapsed((prev) => !prev)}
            >
              {adminSidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/app')}
              className="hidden sm:inline-flex"
            >
              Ver como Usuário
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-600 text-sm">
                      {user?.name?.charAt(0) || 'A'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex">
        {adminSidebarOpen ? (
          <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setAdminSidebarOpen(false)} />
        ) : null}
        <aside className={`fixed md:sticky top-[73px] md:top-[73px] left-0 z-50 h-[calc(100vh-73px)] bg-white border-r border-zinc-200 transition-all ${adminSidebarCollapsed ? 'w-[88px]' : 'w-[260px]'} ${adminSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between p-3 border-b border-zinc-100 md:hidden">
              <p className="text-sm font-semibold text-zinc-900">Menu Admin</p>
              <Button variant="ghost" size="icon" onClick={() => setAdminSidebarOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <nav className="flex-1 space-y-1 p-3 overflow-y-auto">
              {ADMIN_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`w-full flex items-center ${adminSidebarCollapsed ? 'justify-center' : 'justify-between'} gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab.id ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-600 hover:bg-zinc-50'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <span className="flex items-center gap-3 min-w-0">
                    <tab.icon className="w-4 h-4 shrink-0" />
                    {!adminSidebarCollapsed ? <span className="truncate">{tab.label}</span> : null}
                  </span>
                  {!adminSidebarCollapsed && sidebarBadgeMap[tab.id] > 0 ? (
                    <Badge className="bg-red-100 text-red-700 border-0 text-[10px] px-2 py-0">
                      {sidebarBadgeMap[tab.id]}
                    </Badge>
                  ) : null}
                </button>
              ))}
            </nav>
            <div className="border-t border-zinc-100 p-3 space-y-3">
              {!adminSidebarCollapsed ? (
                <>
                  <div className="rounded-xl bg-zinc-50 p-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-zinc-400">Admin</p>
                    <p className="text-sm font-medium text-zinc-900 mt-1">{user?.name || 'Administrador'}</p>
                    <p className="text-xs text-zinc-500">{user?.email}</p>
                  </div>
                  <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/app')}>
                    <Users className="w-4 h-4 mr-2" />
                    Ver como Usuário
                  </Button>
                </>
              ) : (
                <div className="flex justify-center">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-600 text-sm">
                      {user?.name?.charAt(0) || 'A'}
                    </AvatarFallback>
                  </Avatar>
                </div>
              )}
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1 px-4 md:px-5 py-6 md:py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <Card className="border-zinc-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtros da Visão Geral
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-2 min-w-[280px]">
                    <Label className="text-sm">Período dos cadastros</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="h-14 w-full justify-start text-left font-normal">
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {overviewDateRange.from ? (
                            overviewDateRange.to ? (
                              <>
                                {format(overviewDateRange.from, 'dd/MM/yy')} - {format(overviewDateRange.to, 'dd/MM/yy')}
                              </>
                            ) : (
                              format(overviewDateRange.from, 'dd/MM/yy')
                            )
                          ) : (
                            'Selecionar período'
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="range"
                          selected={overviewDateRange}
                          onSelect={(range) => {
                            if (range?.from && range?.to) {
                              setOverviewDateRange(range);
                            }
                          }}
                          locale={ptBR}
                          numberOfMonths={2}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2 min-w-[260px]">
                    <Label className="text-sm">Visualização do gráfico</Label>
                    <div className="grid grid-cols-3 gap-2 rounded-2xl bg-zinc-100 p-1">
                      {[
                        { id: 'day', label: 'Dia' },
                        { id: 'month', label: 'Mês' },
                        { id: 'year', label: 'Ano' },
                      ].map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setRegistrationsView(option.id)}
                          className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                            registrationsView === option.id
                              ? 'bg-white text-zinc-900 shadow-sm'
                              : 'text-zinc-500 hover:text-zinc-800'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {stats?.scale?.health?.status !== 'healthy' || (stats?.scale?.queue?.pending || 0) > 0 || (stats?.scale?.queue?.failed || 0) > 0 ? (
              <Card className={`border ${
                stats?.scale?.health?.status === 'attention' || (stats?.scale?.queue?.failed || 0) > 0
                  ? 'border-red-200 bg-red-50'
                  : 'border-amber-200 bg-amber-50'
              }`}>
                <CardContent className="p-5">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className={`w-5 h-5 ${
                          stats?.scale?.health?.status === 'attention' || (stats?.scale?.queue?.failed || 0) > 0
                            ? 'text-red-600'
                            : 'text-amber-600'
                        }`} />
                        <p className="font-medium text-zinc-900">Monitor de risco operacional</p>
                        <HealthBadge status={stats?.scale?.health?.status} />
                      </div>
                      <p className="text-sm text-zinc-700">
                        Jobs na fila: <strong>{stats?.scale?.queue?.pending || 0}</strong> pendentes, <strong>{stats?.scale?.queue?.processing || 0}</strong> processando, <strong>{stats?.scale?.queue?.failed || 0}</strong> falhos.
                      </p>
                      <p className="text-sm text-zinc-600">
                        {stats?.scale?.health?.recommended_action}
                      </p>
                    </div>
                    <Button variant="outline" onClick={fetchData}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Atualizar saúde
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {/* Stats Grid */}
            <div className="flex flex-wrap items-center gap-2">
              {[
                { id: 'all', label: 'Tudo' },
                { id: 'personal', label: 'Pessoal' },
                { id: 'business', label: 'Business' },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setOverviewSegment(option.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    overviewSegment === option.id
                      ? 'bg-sky-600 text-white shadow-sm'
                      : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
              <Card className="border-zinc-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900">{overviewStats.totalUsers || 0}</p>
                      <p className="text-xs text-zinc-500">
                        {overviewSegment === 'all' ? 'Total Usuários' : overviewSegment === 'business' ? 'Usuários Business' : 'Usuários Pessoal'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-zinc-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900">
                        R$ {overviewStats.mrr || 0}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {overviewSegment === 'all' ? 'MRR' : `MRR ${overviewSegment === 'business' ? 'Business' : 'Pessoal'}`}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-zinc-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900">{overviewUsersInRange.length}</p>
                      <p className="text-xs text-zinc-500">Cadastros no período</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-zinc-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                      <Ban className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900">{overviewCancellationsInRange.length}</p>
                      <p className="text-xs text-zinc-500">Cancelamentos no período</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-zinc-200">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900">{overviewActiveUsers.length}</p>
                      <p className="text-xs text-zinc-500">Planos ativos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Section */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-zinc-200">
                <CardHeader className="flex flex-row items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg">
                      {registrationsView === 'day' ? 'Cadastros e Cancelamentos por Dia' : registrationsView === 'month' ? 'Cadastros e Cancelamentos por Mês' : 'Cadastros e Cancelamentos por Ano'}
                    </CardTitle>
                    <p className="text-sm text-zinc-500 mt-1">
                      {registrationsView === 'day'
                        ? 'Visualização em linha do período selecionado para entrada e perda de base.'
                        : 'Visualização em barras do período selecionado para entrada e perda de base.'}
                    </p>
                  </div>
                  <Badge className="bg-sky-100 text-sky-700 border-0">
                    {overviewUsersInRange.length} cadastro{overviewUsersInRange.length === 1 ? '' : 's'} • {overviewCancellationsInRange.length} cancelamento{overviewCancellationsInRange.length === 1 ? '' : 's'}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <div className="mb-3 flex flex-wrap items-center gap-4 px-1">
                    <div className="flex items-center gap-2 text-xs text-zinc-600">
                      <span className="inline-flex h-2.5 w-2.5 rounded-full bg-blue-600" />
                      Cadastros
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-600">
                      <span className="inline-flex h-2.5 w-2.5 rounded-full bg-orange-500" />
                      Cancelamentos
                    </div>
                  </div>
                  <RegistrationsChart data={getRegistrationsByPeriod()} view={registrationsView} />
                </CardContent>
              </Card>

              <Card className="border-zinc-200">
                <CardHeader>
                  <CardTitle className="text-lg">
                    Distribuição de Planos {overviewSegment === 'all' ? '' : `• ${overviewSegment === 'business' ? 'Business' : 'Pessoal'}`}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { plan: 'Starter', count: overviewStats.byPlan?.starter || 0, color: 'bg-zinc-500', key: 'starter' },
                      { plan: 'Pro', count: overviewStats.byPlan?.pro || 0, color: 'bg-emerald-500', key: 'pro' },
                      { plan: 'Premium', count: overviewStats.byPlan?.business || 0, color: 'bg-amber-500', key: 'business' },
                    ].map((item) => {
                      const total = overviewStats.totalUsers || 1;
                      const percentage = Math.round((item.count / total) * 100);
                      return (
                        <div key={item.plan}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-zinc-600">{item.plan}</span>
                            <span className="font-medium">{item.count} ({percentage}%)</span>
                          </div>
                          <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                            <div className={`h-full ${item.color}`} style={{ width: `${percentage}%` }} />
                          </div>
                          <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-zinc-500">
                            <span>Vendas: <strong className="text-zinc-900">{overviewPlanPerformance.sales[item.key] || 0}</strong></span>
                            <span>Cancel.: <strong className="text-zinc-900">{overviewPlanPerformance.cancellations[item.key] || 0}</strong></span>
                            <span>Ativos: <strong className="text-zinc-900">{overviewPlanPerformance.actives[item.key] || 0}</strong></span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Support Stats */}
            <Card className="border-zinc-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <HeadphonesIcon className="w-5 h-5 text-emerald-600" />
                  Status do Suporte
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-4 bg-orange-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-orange-600">{supportStats?.by_status?.open || 0}</p>
                    <p className="text-sm text-orange-700">Abertos</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-blue-600">{supportStats?.by_status?.in_progress || 0}</p>
                    <p className="text-sm text-blue-700">Em Andamento</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-emerald-600">{supportStats?.by_status?.resolved || 0}</p>
                    <p className="text-sm text-emerald-700">Resolvidos</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-red-600">{supportStats?.sla_breached || 0}</p>
                    <p className="text-sm text-red-700">SLA Estourado</p>
                  </div>
                  <div className="p-4 bg-violet-50 rounded-lg text-center">
                    <p className="text-3xl font-bold text-violet-600">{stats?.billing_requests?.pending || 0}</p>
                    <p className="text-sm text-violet-700">Cancel. Pendentes</p>
                  </div>
                </div>
                
                {supportStats?.avg_response_minutes > 0 && (
                  <div className="mt-4 p-3 bg-zinc-50 rounded-lg">
                    <p className="text-sm text-zinc-600">
                      Tempo médio de resposta: <span className="font-medium">{Math.round(supportStats.avg_response_minutes)} min</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="border-zinc-200 lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-sky-600" />
                    Saúde e Escala do Sistema
                  </CardTitle>
                  <HealthBadge status={stats?.scale?.health?.status} />
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    <AnalyticsKpiCard
                      icon={MessageSquare}
                      label="Mensagens em 30 dias"
                      value={stats?.scale?.messages_last_30_days || 0}
                      helper={`${stats?.usage?.total_messages || 0} totais`}
                      tone="sky"
                    />
                    <AnalyticsKpiCard
                      icon={FileText}
                      label="Resumos em 30 dias"
                      value={stats?.scale?.summaries_last_30_days || 0}
                      helper={`${stats?.scale?.questions_last_30_days || 0} perguntas IA`}
                      tone="violet"
                    />
                    <AnalyticsKpiCard
                      icon={Users}
                      label="Média por grupo"
                      value={stats?.scale?.avg_messages_per_group || 0}
                      helper={`${stats?.scale?.high_volume_groups || 0} grupos > 1000 msgs`}
                      tone="emerald"
                    />
                    <AnalyticsKpiCard
                      icon={Database}
                      label="Dados armazenados"
                      value={`${stats?.scale?.storage?.data_size_mb || 0} MB`}
                      helper={`Índices ${stats?.scale?.storage?.indexes_size_mb || 0} MB`}
                      tone="amber"
                    />
                    <AnalyticsKpiCard
                      icon={RefreshCw}
                      label="Fila pesada"
                      value={stats?.scale?.queue?.pending || 0}
                      helper={`${stats?.scale?.queue?.processing || 0} processando • ${stats?.scale?.queue?.failed || 0} falhos`}
                      tone={(stats?.scale?.queue?.failed || 0) > 0 ? 'rose' : 'sky'}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="rounded-2xl bg-zinc-50 p-4">
                      <p className="text-sm font-medium text-zinc-700 mb-2">Cobertura de índices críticos</p>
                      <p className="text-lg font-semibold text-zinc-900">
                        {stats?.scale?.indexes?.critical_ready || 0}/{stats?.scale?.indexes?.critical_total || 0}
                      </p>
                      <p className="text-sm text-zinc-500 mt-1">
                        {stats?.scale?.indexes?.coverage_pct || 0}% prontos
                      </p>
                    </div>
                    <div className="rounded-2xl bg-zinc-50 p-4">
                      <p className="text-sm font-medium text-zinc-700 mb-2">Storage operacional</p>
                      <p className="text-lg font-semibold text-zinc-900">
                        {stats?.scale?.storage?.storage_size_mb || 0} MB
                      </p>
                      <p className="text-sm text-zinc-500 mt-1">
                        Dados {stats?.scale?.storage?.data_size_mb || 0} MB • Índices {stats?.scale?.storage?.indexes_size_mb || 0} MB
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm font-medium text-zinc-700">Alertas operacionais</p>
                    {(stats?.scale?.health?.alerts || []).length ? (
                      (stats?.scale?.health?.alerts || []).map((alert, index) => (
                        <div key={`${alert}-${index}`} className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                          {alert}
                        </div>
                      ))
                    ) : (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                        Base está leve para o estágio atual. Continue acompanhando esse bloco conforme o número de grupos e resumos subir.
                      </div>
                    )}
                    <p className="text-sm text-zinc-500">
                      {stats?.scale?.health?.recommended_action}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-zinc-200">
                <CardHeader>
                  <CardTitle className="text-lg">Coleções e Índices</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(stats?.scale?.indexes?.collection_index_counts || {}).map(([collectionName, count]) => (
                    <div key={collectionName} className="flex items-center justify-between rounded-xl bg-zinc-50 px-3 py-2">
                      <span className="text-sm text-zinc-600">{collectionName}</span>
                      <Badge className="bg-sky-100 text-sky-700 border-0">{count}</Badge>
                    </div>
                  ))}

                  {(stats?.scale?.indexes?.missing || []).length ? (
                    <div className="pt-2">
                      <p className="text-sm font-medium text-zinc-700 mb-2">Faltando</p>
                      <div className="space-y-2">
                        {(stats?.scale?.indexes?.missing || []).map((item) => (
                          <div key={item} className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl bg-emerald-50 px-3 py-3 text-sm text-emerald-700">
                      Índices críticos criados. Essa base já está mais segura para crescer com Mongo.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Users */}
            <Card className="border-zinc-200">
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-lg">Usuários Recentes</CardTitle>
                <Button variant="ghost" size="sm" className="self-start sm:self-auto" onClick={() => setActiveTab('users')}>
                  Ver todos
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.slice(0, 5).map((u) => (
                    <div 
                      key={u.user_id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-zinc-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="w-9 h-9">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback className="bg-emerald-100 text-emerald-600 text-sm">
                            {u.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-zinc-900 break-words">{u.name}</p>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
                            <p className="text-xs text-zinc-500 break-all">{u.email}</p>
                            <Badge className={`${getUserSegment(u) === 'business' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'} border-0`}>
                              {getUserSegment(u) === 'business' ? 'Business' : 'Pessoal'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="self-start sm:self-auto">
                        {getPlanBadge(u.plan || 'starter')}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <Card className="border-zinc-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtros da Landing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-2 min-w-[280px]">
                    <Label className="text-sm">Período</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="h-14 w-full justify-start text-left font-normal">
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {dateRange.from ? (
                            dateRange.to ? (
                              <>
                                {format(dateRange.from, "dd/MM/yy")} - {format(dateRange.to, "dd/MM/yy")}
                              </>
                            ) : (
                              format(dateRange.from, "dd/MM/yy")
                            )
                          ) : (
                            'Selecionar período'
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="range"
                          selected={dateRange}
                          onSelect={(range) => {
                            if (range?.from && range?.to) {
                              setDateRange(range);
                            }
                          }}
                          locale={ptBR}
                          numberOfMonths={2}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2 min-w-[220px]">
                    <Label className="text-sm">Versão da página</Label>
                    <Select value={analyticsVariantFilter} onValueChange={setAnalyticsVariantFilter}>
                      <SelectTrigger className="h-14 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Geral</SelectItem>
                        <SelectItem value="personal">Pessoal</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 min-w-[220px]">
                    <Label className="text-sm">Dispositivo</Label>
                    <Select value={analyticsDeviceFilter} onValueChange={setAnalyticsDeviceFilter}>
                      <SelectTrigger className="h-14 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="desktop">Desktop</SelectItem>
                        <SelectItem value="mobile">Mobile</SelectItem>
                        <SelectItem value="tablet">Tablet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="min-w-[180px] space-y-2">
                    <Label className="text-sm opacity-0">Atualizar</Label>
                    <Button variant="outline" onClick={fetchLandingAnalytics} className="h-14 w-full">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Atualizar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {analyticsLoading ? (
              <div className="min-h-[240px] rounded-2xl border border-zinc-200 bg-white flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-sky-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  <AnalyticsKpiCard
                    icon={Globe2}
                    label="Visitantes únicos"
                    value={landingAnalytics?.kpis?.unique_visitors || 0}
                    helper={`${landingAnalytics?.kpis?.sessions || 0} sessões`}
                    tone="sky"
                  />
                  <AnalyticsKpiCard
                    icon={MousePointerClick}
                    label="Cliques em CTA"
                    value={landingAnalytics?.kpis?.cta_clicks || 0}
                    helper={`Taxa ${landingAnalytics?.kpis?.cta_rate || 0}%`}
                    tone="emerald"
                  />
                  <AnalyticsKpiCard
                    icon={Route}
                    label="Profundidade média"
                    value={landingAnalytics?.kpis?.avg_journey_depth || 0}
                    helper="seções por sessão"
                    tone="violet"
                  />
                  <AnalyticsKpiCard
                    icon={Clock3}
                    label="Tempo médio"
                    value={landingAnalytics?.kpis?.avg_session_duration_label || '0s'}
                    helper={`Bounce ${landingAnalytics?.kpis?.bounce_rate || 0}%`}
                    tone="amber"
                  />
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                  <Card className="border-zinc-200 lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-lg">Tráfego por dia</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <SimpleBarChart data={landingAnalytics?.timeline || []} title="" />
                    </CardContent>
                  </Card>

                  <Card className="border-zinc-200">
                    <CardHeader>
                      <CardTitle className="text-lg">KPIs de lead</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between rounded-xl bg-zinc-50 px-4 py-3">
                        <span className="text-sm text-zinc-600">Visualizações da página</span>
                        <span className="font-semibold text-zinc-900">{landingAnalytics?.kpis?.page_views || 0}</span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-zinc-50 px-4 py-3">
                        <span className="text-sm text-zinc-600">Cliques para WhatsApp</span>
                        <span className="font-semibold text-zinc-900">{landingAnalytics?.kpis?.whatsapp_clicks || 0}</span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-zinc-50 px-4 py-3">
                        <span className="text-sm text-zinc-600">Cliques para entrar/testar</span>
                        <span className="font-semibold text-zinc-900">{landingAnalytics?.kpis?.login_or_signup_clicks || 0}</span>
                      </div>
                      <div className="flex items-center justify-between rounded-xl bg-zinc-50 px-4 py-3">
                        <span className="text-sm text-zinc-600">Visitantes únicos por dia</span>
                        <span className="font-semibold text-zinc-900">{landingAnalytics?.kpis?.daily_unique_visitors || 0}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  <Card className="border-zinc-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Mapa de acessos</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <SimpleGeoMap locations={landingAnalytics?.locations || []} />
                      <div className="space-y-2">
                        {(landingAnalytics?.locations || []).slice(0, 6).map((location, index) => (
                          <div key={`${location.country}-${location.city}-${index}`} className="flex items-center justify-between rounded-lg bg-zinc-50 px-3 py-2">
                            <div className="flex items-center gap-2 text-sm text-zinc-700">
                              <MapPin className="w-4 h-4 text-sky-600" />
                              <span>{[location.city, location.region, location.country].filter(Boolean).join(', ')}</span>
                            </div>
                            <span className="text-sm font-semibold text-zinc-900">{location.count}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <div className="space-y-6">
                    <Card className="border-zinc-200">
                      <CardHeader>
                        <CardTitle className="text-lg">Seções mais visitadas</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <SimpleBarChart data={landingAnalytics?.top_sections || []} title="" />
                      </CardContent>
                    </Card>

                    <Card className="border-zinc-200">
                      <CardHeader>
                        <CardTitle className="text-lg">CTAs mais clicados</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <SimpleBarChart data={landingAnalytics?.top_ctas || []} title="" />
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                  <Card className="border-zinc-200">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Smartphone className="w-5 h-5 text-sky-600" />
                        Dispositivos
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <SimpleBarChart data={landingAnalytics?.devices || []} title="" />
                    </CardContent>
                  </Card>

                  <Card className="border-zinc-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Navegadores</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <SimpleBarChart data={landingAnalytics?.browsers || []} title="" />
                    </CardContent>
                  </Card>

                  <Card className="border-zinc-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Origem dos leads</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <SimpleBarChart data={landingAnalytics?.sources || []} title="" />
                    </CardContent>
                  </Card>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  <Card className="border-zinc-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Jornadas mais comuns</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {(landingAnalytics?.journeys || []).length ? (landingAnalytics?.journeys || []).map((journey, index) => (
                        <div key={`${journey.label}-${index}`} className="rounded-xl bg-zinc-50 px-4 py-3">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm text-zinc-700">{journey.label}</p>
                            <Badge className="bg-sky-100 text-sky-700 border-0">{journey.value}</Badge>
                          </div>
                        </div>
                      )) : (
                        <p className="text-sm text-zinc-500">Ainda sem dados suficientes de jornada.</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-zinc-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Sessões recentes</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {(landingAnalytics?.recent_sessions || []).length ? (landingAnalytics?.recent_sessions || []).map((session) => (
                        <div key={session.session_id} className="rounded-xl border border-zinc-100 bg-white px-4 py-3">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <span className="text-sm font-medium text-zinc-900">{session.source}</span>
                            <Badge className="bg-zinc-100 text-zinc-700 border-0">{session.device_type}</Badge>
                          </div>
                          <p className="text-sm text-zinc-600">{session.journey}</p>
                          <div className="mt-2 flex items-center justify-between text-xs text-zinc-500">
                            <span>{session.events} eventos</span>
                            <span>{session.duration_label}</span>
                          </div>
                        </div>
                      )) : (
                        <p className="text-sm text-zinc-500">Sem sessões registradas neste filtro.</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search */}
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                  className="pl-10"
                />
              </div>
              <div className="w-full md:w-[220px]">
                <Select value={usersSegmentFilter} onValueChange={setUsersSegmentFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Segmento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os segmentos</SelectItem>
                    <SelectItem value="personal">Pessoal</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={searchUsers}>Buscar</Button>
            </div>

            {/* Users List */}
            <Card className="border-zinc-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr>
                        <th className="text-left p-4 text-sm font-medium text-zinc-600">Usuário</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-600">Segmento</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-600">Plano</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-600">Tempo no plano</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-600">Status</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-600">Tempo na plataforma</th>
                        <th className="text-left p-4 text-sm font-medium text-zinc-600"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-9 h-9">
                                <AvatarImage src={u.picture} />
                                <AvatarFallback className="bg-emerald-100 text-emerald-600 text-sm">
                                  {u.name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium text-zinc-900">{u.name}</p>
                                <p className="text-xs text-zinc-500">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge className={`${getUserSegment(u) === 'business' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'} border-0`}>
                              {getUserSegment(u) === 'business' ? 'Business' : 'Pessoal'}
                            </Badge>
                          </td>
                          <td className="p-4">{getPlanBadge(u.plan || 'starter')}</td>
                          <td className="p-4 text-sm text-zinc-500">
                            {getElapsedLabel(u.plan_started_at || u.created_at)}
                          </td>
                          <td className="p-4">
                            <Badge className={`${u.plan_status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-700'} border-0`}>
                              {u.plan_status || 'active'}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm text-zinc-500">
                            {getElapsedLabel(u.created_at)}
                          </td>
                          <td className="p-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/admin/users/${u.user_id}`)}
                            >
                              Ver detalhes
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="space-y-6">
            <Card className="border-zinc-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtros de Pagamentos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-2 min-w-[220px]">
                    <Label className="text-sm">Data inicial</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="h-14 w-full justify-start text-left font-normal" disabled={!paymentsUseDateFilter}>
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {!paymentsUseDateFilter
                            ? 'Geral'
                            : paymentsDateRange.from
                              ? format(paymentsDateRange.from, 'dd/MM/yy')
                              : 'Selecionar início'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={paymentsDateRange.from}
                          onSelect={handlePaymentsFromDateSelect}
                          locale={ptBR}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2 min-w-[220px]">
                    <Label className="text-sm">Data final</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="h-14 w-full justify-start text-left font-normal" disabled={!paymentsUseDateFilter}>
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {!paymentsUseDateFilter
                            ? 'Geral'
                            : paymentsDateRange.to
                              ? format(paymentsDateRange.to, 'dd/MM/yy')
                              : 'Selecionar fim'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={paymentsDateRange.to}
                          onSelect={handlePaymentsToDateSelect}
                          locale={ptBR}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2 min-w-[260px]">
                    <Label className="text-sm">Visualização</Label>
                    <div className="grid grid-cols-3 gap-2 rounded-2xl bg-zinc-100 p-1">
                      {[
                        { id: 'day', label: 'Dia' },
                        { id: 'month', label: 'Mês' },
                        { id: 'year', label: 'Ano' },
                      ].map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setPaymentsView(option.id)}
                          className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                            paymentsView === option.id
                              ? 'bg-white text-zinc-900 shadow-sm'
                              : 'text-zinc-500 hover:text-zinc-800'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2 min-w-[220px]">
                    <Label className="text-sm">Segmento</Label>
                    <Select value={paymentsSegmentFilter} onValueChange={setPaymentsSegmentFilter}>
                      <SelectTrigger className="h-14 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Geral</SelectItem>
                        <SelectItem value="personal">Pessoal</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 min-w-[180px]">
                    <Label className="text-sm">Modo</Label>
                    <Button
                      type="button"
                      variant={paymentsUseDateFilter ? 'outline' : 'default'}
                      onClick={() => setPaymentsUseDateFilter((prev) => !prev)}
                      className="h-14 w-full"
                    >
                      {paymentsUseDateFilter ? 'Ver geral' : 'Usar filtro de data'}
                    </Button>
                  </div>

                  <div className="min-w-[180px] space-y-2">
                    <Label className="text-sm opacity-0">Atualizar</Label>
                    <Button variant="outline" onClick={fetchPaymentsDashboard} className="h-14 w-full">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Atualizar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {paymentsLoading ? (
              <div className="min-h-[240px] rounded-2xl border border-zinc-200 bg-white flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-sky-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  <AnalyticsKpiCard
                    icon={DollarSign}
                    label={paymentsReceivedLabel}
                    value={`R$ ${paymentsReceivedValue.toFixed(2).replace('.', ',')}`}
                    helper={paymentsReceivedHelper}
                    tone="emerald"
                  />
                  <AnalyticsKpiCard
                    icon={TrendingUp}
                    label={paymentsForecastLabel}
                    value={`R$ ${paymentsForecastValue.toFixed(2).replace('.', ',')}`}
                    helper={paymentsForecastHelper}
                    tone="sky"
                  />
                  <AnalyticsKpiCard
                    icon={Users}
                    label="Vendas no período"
                    value={paymentsData?.dashboard?.kpis?.sold_count || 0}
                    helper="Novas assinaturas no recorte"
                    tone="violet"
                  />
                  <AnalyticsKpiCard
                    icon={Ban}
                    label="Cancelamentos no período"
                    value={paymentsData?.dashboard?.kpis?.cancelled_count || 0}
                    helper="Pedidos/processos de cancelamento"
                    tone="amber"
                  />
                  <AnalyticsKpiCard
                    icon={CreditCard}
                    label="Planos ativos"
                    value={paymentsData?.dashboard?.kpis?.active_count || 0}
                    helper={`R$ ${(paymentsData?.dashboard?.kpis?.annual_contract_value || 0).toFixed(2).replace('.', ',')} em contrato anualizado`}
                    tone="sky"
                  />
                  <AnalyticsKpiCard
                    icon={AlertTriangle}
                    label="Inadimplentes"
                    value={paymentsData?.dashboard?.kpis?.overdue_count || 0}
                    helper={`R$ ${(paymentsData?.dashboard?.kpis?.overdue_amount || 0).toFixed(2).replace('.', ',')} em risco`}
                    tone={(paymentsData?.dashboard?.kpis?.overdue_count || 0) > 0 ? 'rose' : 'amber'}
                  />
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                  <Card className="border-zinc-200 lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-lg">Recebido x Previsível</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <PaymentComparisonChart data={paymentsData?.dashboard?.timeline || []} view={paymentsView} />
                    </CardContent>
                  </Card>

                  <Card className="border-zinc-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Receita por Plano</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {[
                        { key: 'starter', label: 'Starter', note: 'Mensal ou anual', tone: 'bg-zinc-50 text-zinc-700' },
                        { key: 'pro', label: 'Pro', note: 'Mensal ou anual', tone: 'bg-emerald-50 text-emerald-700' },
                        { key: 'business', label: 'Premium', note: 'Mensal ou anual', tone: 'bg-amber-50 text-amber-700' },
                      ].map((item) => (
                        <div key={item.key} className={`rounded-xl p-4 ${item.tone}`}>
                          <div className="flex flex-col gap-3 mb-3 lg:flex-row lg:items-start lg:justify-between">
                            <div className="min-w-0">
                              <p className="font-semibold">{item.label}</p>
                              <p className="text-sm opacity-80">{item.note}</p>
                            </div>
                            <div className="min-w-0 lg:max-w-[220px] text-left lg:text-right break-words">
                              <p className="text-lg font-bold">
                                {`R$ ${Number(paymentsData?.dashboard?.by_plan?.[item.key]?.received_amount || 0).toFixed(2).replace('.', ',')}`}
                              </p>
                              <p className="text-xs opacity-75 leading-relaxed">
                                Recebido • {paymentsData?.dashboard?.by_plan?.[item.key]?.count || 0} clientes
                              </p>
                              <p className="text-xs opacity-75 mt-1 leading-relaxed">
                                Previsto • R$ {Number(paymentsData?.dashboard?.by_plan?.[item.key]?.forecast_amount || 0).toFixed(2).replace('.', ',')}
                              </p>
                              <div className="mt-2 flex items-center justify-end gap-2 text-[11px] opacity-75 whitespace-nowrap">
                                <span>Vendido • {paymentsData?.dashboard?.by_plan?.[item.key]?.sold_count || 0}</span>
                                <span>Cancelado • {paymentsData?.dashboard?.by_plan?.[item.key]?.cancelled_count || 0}</span>
                                <span>Ativo • {paymentsData?.dashboard?.by_plan?.[item.key]?.active_count || 0}</span>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                              <div className="rounded-lg bg-white/70 px-3 py-2">
                                <p className="text-[11px] uppercase tracking-wide opacity-70">Mensal</p>
                                <p className="text-sm font-semibold">
                                  R$ {Number(paymentsData?.dashboard?.by_plan?.[item.key]?.monthly_amount || 0).toFixed(2).replace('.', ',')}
                                </p>
                                <p className="text-[11px] opacity-70">Recebido • {paymentsData?.dashboard?.by_plan?.[item.key]?.monthly_count || 0} clientes</p>
                                <p className="text-[11px] opacity-70 mt-1">
                                  Previsto • R$ {Number(paymentsData?.dashboard?.by_plan?.[item.key]?.monthly_forecast_amount || 0).toFixed(2).replace('.', ',')}
                                </p>
                              </div>
                              <div className="rounded-lg bg-white/70 px-3 py-2">
                                <p className="text-[11px] uppercase tracking-wide opacity-70">Anual</p>
                                <p className="text-sm font-semibold">
                                  R$ {Number(paymentsData?.dashboard?.by_plan?.[item.key]?.annual_amount || 0).toFixed(2).replace('.', ',')}
                                </p>
                                <p className="text-[11px] opacity-70">Recebido • {paymentsData?.dashboard?.by_plan?.[item.key]?.annual_count || 0} clientes</p>
                                <p className="text-[11px] opacity-70 mt-1">
                                  Previsto • R$ {Number(paymentsData?.dashboard?.by_plan?.[item.key]?.annual_forecast_amount || 0).toFixed(2).replace('.', ',')}
                                </p>
                              </div>
                            </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  <Card className="border-zinc-200">
                    <CardHeader>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <CardTitle className="text-lg">Distribuição da receita por plano</CardTitle>
                        <Select value={paymentsDistributionMode} onValueChange={setPaymentsDistributionMode}>
                          <SelectTrigger className="w-[170px] h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="received">Recebido</SelectItem>
                            <SelectItem value="forecast">Previsto</SelectItem>
                            <SelectItem value="sold">Vendido</SelectItem>
                            <SelectItem value="cancelled">Cancelado</SelectItem>
                            <SelectItem value="active">Ativo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardHeader>
                    <CardContent className="grid lg:grid-cols-[280px_1fr] gap-6 items-center">
                      <PaymentDistributionPie
                        data={[
                          { label: 'Starter', value: Number(paymentsData?.dashboard?.by_plan?.starter?.[paymentsDistributionMode === 'received' ? 'received_amount' : paymentsDistributionMode === 'forecast' ? 'forecast_amount' : paymentsDistributionMode === 'sold' ? 'sold_count' : paymentsDistributionMode === 'cancelled' ? 'cancelled_count' : 'active_count'] || 0) },
                          { label: 'Pro', value: Number(paymentsData?.dashboard?.by_plan?.pro?.[paymentsDistributionMode === 'received' ? 'received_amount' : paymentsDistributionMode === 'forecast' ? 'forecast_amount' : paymentsDistributionMode === 'sold' ? 'sold_count' : paymentsDistributionMode === 'cancelled' ? 'cancelled_count' : 'active_count'] || 0) },
                          { label: 'Premium', value: Number(paymentsData?.dashboard?.by_plan?.business?.[paymentsDistributionMode === 'received' ? 'received_amount' : paymentsDistributionMode === 'forecast' ? 'forecast_amount' : paymentsDistributionMode === 'sold' ? 'sold_count' : paymentsDistributionMode === 'cancelled' ? 'cancelled_count' : 'active_count'] || 0) },
                        ]}
                      />
                      <div className="space-y-3">
                        {[
                          { key: 'starter', label: 'Starter', color: PIE_COLORS[0] },
                          { key: 'pro', label: 'Pro', color: PIE_COLORS[1] },
                          { key: 'business', label: 'Premium', color: PIE_COLORS[2] },
                        ].map((item) => (
                          <div key={item.key} className="flex items-center justify-between rounded-xl bg-zinc-50 px-4 py-3">
                            <div className="flex items-center gap-3">
                              <span className="inline-flex h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                              <span className="text-sm text-zinc-700">{item.label}</span>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-zinc-900">
                                {paymentsDistributionMode === 'received' || paymentsDistributionMode === 'forecast'
                                  ? `R$ ${Number(paymentsData?.dashboard?.by_plan?.[item.key]?.[paymentsDistributionMode === 'received' ? 'received_amount' : 'forecast_amount'] || 0).toFixed(2).replace('.', ',')}`
                                  : Number(paymentsData?.dashboard?.by_plan?.[item.key]?.[paymentsDistributionMode === 'sold' ? 'sold_count' : paymentsDistributionMode === 'cancelled' ? 'cancelled_count' : 'active_count'] || 0)}
                              </p>
                              <p className="text-xs text-zinc-500">
                                {paymentsDistributionMode === 'received' || paymentsDistributionMode === 'forecast'
                                  ? `${paymentsData?.dashboard?.by_plan?.[item.key]?.count || 0} clientes`
                                  : 'clientes'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-zinc-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Distribuição por modalidade</CardTitle>
                    </CardHeader>
                    <CardContent className="grid lg:grid-cols-[280px_1fr] gap-6 items-center">
                      <PaymentDistributionPie
                        data={[
                          { label: 'Mensal', value: Number(paymentsData?.dashboard?.by_billing_cycle?.monthly?.[paymentsDistributionMode === 'received' ? 'received_amount' : paymentsDistributionMode === 'forecast' ? 'forecast_amount' : paymentsDistributionMode === 'sold' ? 'sold_count' : paymentsDistributionMode === 'cancelled' ? 'cancelled_count' : 'active_count'] || 0) },
                          { label: 'Anual', value: Number(paymentsData?.dashboard?.by_billing_cycle?.annual?.[paymentsDistributionMode === 'received' ? 'received_amount' : paymentsDistributionMode === 'forecast' ? 'forecast_amount' : paymentsDistributionMode === 'sold' ? 'sold_count' : paymentsDistributionMode === 'cancelled' ? 'cancelled_count' : 'active_count'] || 0) },
                        ]}
                      />
                      <div className="space-y-3">
                        {[
                          { key: 'monthly', label: 'Mensal', color: PIE_COLORS[0] },
                          { key: 'annual', label: 'Anual', color: PIE_COLORS[1] },
                        ].map((item) => (
                          <div key={item.key} className="flex items-center justify-between rounded-xl bg-zinc-50 px-4 py-3">
                            <div className="flex items-center gap-3">
                              <span className="inline-flex h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                              <span className="text-sm text-zinc-700">{item.label}</span>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-zinc-900">
                                {paymentsDistributionMode === 'received' || paymentsDistributionMode === 'forecast'
                                  ? `R$ ${Number(paymentsData?.dashboard?.by_billing_cycle?.[item.key]?.[paymentsDistributionMode === 'received' ? 'received_amount' : 'forecast_amount'] || 0).toFixed(2).replace('.', ',')}`
                                  : Number(paymentsData?.dashboard?.by_billing_cycle?.[item.key]?.[paymentsDistributionMode === 'sold' ? 'sold_count' : paymentsDistributionMode === 'cancelled' ? 'cancelled_count' : 'active_count'] || 0)}
                              </p>
                              <p className="text-xs text-zinc-500">
                                {paymentsDistributionMode === 'received'
                                  ? (paymentsData?.dashboard?.by_billing_cycle?.[item.key]?.received_count || 0)
                                  : paymentsDistributionMode === 'forecast'
                                    ? (paymentsData?.dashboard?.by_billing_cycle?.[item.key]?.forecast_count || 0)
                                    : (paymentsData?.dashboard?.by_billing_cycle?.[item.key]?.[paymentsDistributionMode === 'sold' ? 'sold_count' : paymentsDistributionMode === 'cancelled' ? 'cancelled_count' : 'active_count'] || 0)} clientes
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  <Card className="border-zinc-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Régua de inadimplência</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {(paymentsData?.dashboard?.delinquency?.buckets || []).map((bucket) => (
                        <div key={bucket.label} className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                          <div className="flex items-center justify-between gap-3 mb-1">
                            <p className="text-sm font-medium text-zinc-900">{bucket.label}</p>
                            <Badge className="bg-red-100 text-red-700 border-0">
                              {bucket.count} cliente{bucket.count === 1 ? '' : 's'}
                            </Badge>
                          </div>
                          <p className="text-sm text-zinc-600">R$ {Number(bucket.amount || 0).toFixed(2).replace('.', ',')} em aberto</p>
                          <p className="text-xs text-zinc-500 mt-2">{bucket.action}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Card className="border-zinc-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Clientes em atraso</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {(paymentsData?.dashboard?.delinquency?.accounts || []).length ? (
                        (paymentsData?.dashboard?.delinquency?.accounts || []).map((account) => (
                          <div key={account.user_id} className="rounded-xl border border-zinc-100 bg-white px-4 py-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-medium text-zinc-900">{account.name}</p>
                                <p className="text-xs text-zinc-500">{account.email}</p>
                              </div>
                              <Badge className="bg-red-100 text-red-700 border-0">{account.days_overdue}d atraso</Badge>
                            </div>
                            <div className="mt-2 flex items-center justify-between text-sm text-zinc-600">
                              <span>{account.plan} • {account.billing_cycle === 'annual' ? 'anual' : 'mensal'}</span>
                              <span className="font-semibold">R$ {Number(account.amount_due || 0).toFixed(2).replace('.', ',')}</span>
                            </div>
                            <p className="mt-2 text-xs text-zinc-500">{account.rule?.action}</p>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-xl bg-emerald-50 px-4 py-4 text-sm text-emerald-700">
                          Nenhum cliente em atraso no momento.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <Card className="border-zinc-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Cobranças recentes</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {(paymentsData?.dashboard?.recent_charges || []).length ? (
                      <>
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                            <p className="text-sm font-medium text-zinc-900">Efetivado</p>
                          </div>
                          {(paymentsData?.dashboard?.recent_charges || []).filter((charge) => charge.status === 'received').slice(0, 8).map((charge, index) => (
                        <div key={`${charge.email}-${charge.charge_date}-${index}`} className="flex flex-col gap-2 rounded-xl bg-zinc-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
                          <div>
                            <p className="text-sm font-medium text-zinc-900">{charge.user_name}</p>
                            <p className="text-xs text-zinc-500">{charge.email}</p>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-zinc-600">
                            <span>{charge.plan} • {charge.billing_cycle === 'annual' ? 'anual' : 'mensal'}</span>
                            <span>{format(parseISO(charge.charge_date), 'dd/MM/yyyy')}</span>
                            <Badge className="bg-emerald-100 text-emerald-700 border-0">
                              Recebido
                            </Badge>
                            <span className="font-semibold text-zinc-900">R$ {Number(charge.amount || 0).toFixed(2).replace('.', ',')}</span>
                          </div>
                        </div>
                          ))}
                          {(paymentsData?.dashboard?.recent_charges || []).filter((charge) => charge.status === 'received').length === 0 ? (
                            <div className="rounded-xl bg-zinc-50 px-4 py-4 text-sm text-zinc-500">
                              Nada efetivado neste recorte.
                            </div>
                          ) : null}
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-sky-500" />
                            <p className="text-sm font-medium text-zinc-900">Previsto</p>
                          </div>
                          {(paymentsData?.dashboard?.recent_charges || []).filter((charge) => charge.status === 'forecast').slice(0, 8).map((charge, index) => (
                            <div key={`${charge.email}-${charge.charge_date}-${index}`} className="flex flex-col gap-2 rounded-xl bg-zinc-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
                              <div>
                                <p className="text-sm font-medium text-zinc-900">{charge.user_name}</p>
                                <p className="text-xs text-zinc-500">{charge.email}</p>
                              </div>
                              <div className="flex items-center gap-3 text-sm text-zinc-600">
                                <span>{charge.plan} • {charge.billing_cycle === 'annual' ? 'anual' : 'mensal'}</span>
                                <span>{format(parseISO(charge.charge_date), 'dd/MM/yyyy')}</span>
                                <Badge className="bg-sky-100 text-sky-700 border-0">
                                  Previsto
                                </Badge>
                                <span className="font-semibold text-zinc-900">R$ {Number(charge.amount || 0).toFixed(2).replace('.', ',')}</span>
                              </div>
                            </div>
                          ))}
                          {(paymentsData?.dashboard?.recent_charges || []).filter((charge) => charge.status === 'forecast').length === 0 ? (
                            <div className="rounded-xl bg-zinc-50 px-4 py-4 text-sm text-zinc-500">
                              Nada previsto neste recorte.
                            </div>
                          ) : null}
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
                            <p className="text-sm font-medium text-zinc-900">Cancelado</p>
                          </div>
                          {(paymentsData?.dashboard?.recent_charges || []).filter((charge) => charge.status === 'cancelled').slice(0, 8).map((charge, index) => (
                            <div key={`${charge.email}-${charge.charge_date}-${index}`} className="flex flex-col gap-2 rounded-xl bg-zinc-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
                              <div>
                                <p className="text-sm font-medium text-zinc-900">{charge.user_name}</p>
                                <p className="text-xs text-zinc-500">{charge.email}</p>
                              </div>
                              <div className="flex items-center gap-3 text-sm text-zinc-600">
                                <span>{charge.plan} • {charge.billing_cycle === 'annual' ? 'anual' : 'mensal'}</span>
                                <span>{format(parseISO(charge.charge_date), 'dd/MM/yyyy')}</span>
                                <Badge className="bg-amber-100 text-amber-700 border-0">
                                  Cancelado
                                </Badge>
                                <span className="font-semibold text-zinc-900">R$ {Number(charge.amount || 0).toFixed(2).replace('.', ',')}</span>
                              </div>
                            </div>
                          ))}
                          {(paymentsData?.dashboard?.recent_charges || []).filter((charge) => charge.status === 'cancelled').length === 0 ? (
                            <div className="rounded-xl bg-zinc-50 px-4 py-4 text-sm text-zinc-500">
                              Nada cancelado neste recorte.
                            </div>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <div className="rounded-xl bg-zinc-50 px-4 py-4 text-sm text-zinc-500">
                        Ainda sem cobranças registradas nesse recorte.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {activeTab === 'billing-requests' && (
          <div className="space-y-6">
            <Card className="border-zinc-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtro de Cancelamentos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-2 min-w-[220px]">
                    <Label className="text-sm">Segmento</Label>
                    <Select value={billingRequestsSegmentFilter} onValueChange={setBillingRequestsSegmentFilter}>
                      <SelectTrigger className="h-12 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Geral</SelectItem>
                        <SelectItem value="personal">Pessoal</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-[180px] space-y-2">
                    <Label className="text-sm opacity-0">Atualizar</Label>
                    <Button variant="outline" onClick={fetchBillingRequests} className="h-12 w-full">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Atualizar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-zinc-200">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">{billingRequestsStats?.by_status?.requested || 0}</p>
                  <p className="text-sm text-zinc-500">Novas solicitações</p>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{billingRequestsStats?.by_status?.in_progress || 0}</p>
                  <p className="text-sm text-zinc-500">Em tratamento</p>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{billingRequestsStats?.by_status?.payment_cancelled || 0}</p>
                  <p className="text-sm text-zinc-500">Pagamento cancelado</p>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{billingRequestsStats?.sla_breached || 0}</p>
                  <p className="text-sm text-zinc-500">SLA 1h estourado</p>
                </CardContent>
              </Card>
            </div>

            <Card className="border-zinc-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Kanban de cancelamentos e reativações</CardTitle>
                <Button variant="outline" onClick={fetchBillingRequests}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar
                </Button>
              </CardHeader>
              <CardContent className="p-4">
                {billingRequestsLoading ? (
                  <div className="min-h-[240px] flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <div className="grid md:grid-cols-4 gap-4">
                    {[
                      { key: 'requested', label: 'Solicitado', tone: 'border-orange-200 bg-orange-50/50' },
                      { key: 'in_progress', label: 'Em tratamento', tone: 'border-blue-200 bg-blue-50/50' },
                      { key: 'payment_cancelled', label: 'Pagamento cancelado', tone: 'border-amber-200 bg-amber-50/50' },
                      { key: 'completed', label: 'Concluído', tone: 'border-emerald-200 bg-emerald-50/50' },
                    ].map((column) => {
                      const items = billingRequests.filter((item) => item.status === column.key);
                      return (
                        <div
                          key={column.key}
                          className={`rounded-2xl border p-3 transition-colors ${column.tone} ${dragOverBillingColumn === column.key ? 'ring-2 ring-violet-300' : ''}`}
                          onDragOver={(event) => {
                            event.preventDefault();
                            setDragOverBillingColumn(column.key);
                          }}
                          onDragLeave={() => setDragOverBillingColumn(null)}
                          onDrop={(event) => {
                            event.preventDefault();
                            handleBillingDrop(column.key);
                          }}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-semibold text-zinc-900">{column.label}</p>
                            <Badge className="bg-white text-zinc-700 border-0">{items.length}</Badge>
                          </div>
                          <div className="space-y-3">
                            {items.length ? items.map((requestItem) => {
                              const timeInfo = getBillingRequestTimeInfo(requestItem);
                              return (
                                <button
                                  key={requestItem.request_id}
                                  type="button"
                                  draggable
                                  onDragStart={() => handleBillingDragStart(requestItem.request_id)}
                                  onDragEnd={handleBillingDragEnd}
                                  onClick={() => {
                                    setSelectedBillingRequest(requestItem);
                                    setBillingRequestNotes(requestItem.admin_notes || '');
                                  }}
                                  className="w-full rounded-xl border border-white bg-white p-4 text-left shadow-sm hover:border-violet-300 transition-colors"
                                >
                                  <div className="flex items-center justify-between gap-2 mb-2">
                                    {getBillingRequestTypeBadge(requestItem.request_type)}
                                    {getBillingRequestStatusBadge(requestItem.status)}
                                  </div>
                                  <p className="font-medium text-zinc-900">{requestItem.user_name}</p>
                                  <p className="text-xs text-zinc-500">{requestItem.email}</p>
                                  <p className="text-xs text-zinc-500 mt-1">
                                    {requestItem.plan} • {requestItem.billing_cycle === 'annual' ? 'anual' : 'mensal'}
                                  </p>
                                  {requestItem.reason ? (
                                    <p className="text-sm text-zinc-700 mt-3 line-clamp-3">{requestItem.reason}</p>
                                  ) : null}
                                  <div className="mt-3 text-xs text-zinc-500 space-y-1">
                                    <p>Solicitado em {format(parseISO(requestItem.created_at), 'dd/MM/yyyy HH:mm')}</p>
                                    {requestItem.effective_at ? (
                                      <p>Acesso até {format(parseISO(requestItem.effective_at), 'dd/MM/yyyy')}</p>
                                    ) : null}
                                  </div>
                                  {timeInfo ? (
                                    <div className={`mt-3 rounded-lg px-3 py-2 text-xs font-medium ${timeInfo.tone}`}>
                                      {timeInfo.label}
                                    </div>
                                  ) : null}
                                </button>
                              );
                            }) : (
                              <div className="rounded-xl border border-dashed border-zinc-300 p-4 text-center text-sm text-zinc-500">
                                Sem itens nesta coluna
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'support' && (
          <div className="space-y-6">
            <Card className="border-zinc-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtro de Suporte
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-2 min-w-[220px]">
                    <Label className="text-sm">Segmento</Label>
                    <Select value={supportSegmentFilter} onValueChange={setSupportSegmentFilter}>
                      <SelectTrigger className="h-12 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Geral</SelectItem>
                        <SelectItem value="personal">Pessoal</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="min-w-[180px] space-y-2">
                    <Label className="text-sm opacity-0">Atualizar</Label>
                    <Button variant="outline" onClick={fetchSupportSegmentStats} className="h-12 w-full">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Atualizar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Support Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-zinc-200">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">{currentSupportStats?.by_status?.open || 0}</p>
                  <p className="text-sm text-zinc-500">Abertos</p>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{currentSupportStats?.by_status?.in_progress || 0}</p>
                  <p className="text-sm text-zinc-500">Em Andamento</p>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{currentSupportStats?.by_status?.resolved || 0}</p>
                  <p className="text-sm text-zinc-500">Resolvidos</p>
                </CardContent>
              </Card>
              <Card className="border-zinc-200">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{currentSupportStats?.sla_breached || 0}</p>
                  <p className="text-sm text-zinc-500">SLA Estourado</p>
                </CardContent>
              </Card>
            </div>

            <Button onClick={() => navigate('/admin/support')}>
              <HeadphonesIcon className="w-4 h-4 mr-2" />
              Ir para Central de Suporte
            </Button>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-zinc-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings2 className="w-5 h-5 text-violet-600" />
                    Usuário administrador
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-14 h-14">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-violet-100 text-violet-700">
                        {user?.name?.charAt(0) || 'A'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-zinc-900">{user?.name || 'Administrador'}</p>
                      <p className="text-sm text-zinc-500">{user?.email}</p>
                      <Badge className="mt-2 bg-red-100 text-red-700 border-0">Admin master</Badge>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="rounded-xl bg-zinc-50 p-4">
                      <p className="text-sm text-zinc-500">Ambiente</p>
                      <p className="text-lg font-semibold text-zinc-900">Local / Teste</p>
                    </div>
                    <div className="rounded-xl bg-zinc-50 p-4">
                      <p className="text-sm text-zinc-500">Ações rápidas</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={fetchData}>Atualizar dados</Button>
                        <Button size="sm" variant="outline" onClick={logout}>Sair</Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-zinc-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MonitorCog className="w-5 h-5 text-sky-600" />
                    Sistema
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="rounded-xl bg-zinc-50 p-4">
                      <p className="text-sm text-zinc-500">API</p>
                      <p className="text-sm font-medium text-zinc-900 break-all">{API}</p>
                    </div>
                    <div className="rounded-xl bg-zinc-50 p-4">
                      <p className="text-sm text-zinc-500">Saúde atual</p>
                      <div className="mt-2"><HealthBadge status={stats?.scale?.health?.status} /></div>
                    </div>
                    <div className="rounded-xl bg-zinc-50 p-4">
                      <p className="text-sm text-zinc-500">Mensagens totais</p>
                      <p className="text-lg font-semibold text-zinc-900">{stats?.usage?.total_messages || 0}</p>
                    </div>
                    <div className="rounded-xl bg-zinc-50 p-4">
                      <p className="text-sm text-zinc-500">Jobs na fila</p>
                      <p className="text-lg font-semibold text-zinc-900">{stats?.scale?.queue?.pending || 0}</p>
                    </div>
                  </div>
                  <div className="rounded-xl border border-zinc-200 p-4">
                    <p className="text-sm font-medium text-zinc-800 mb-2">Observações</p>
                    <ul className="space-y-2 text-sm text-zinc-600">
                      <li>O admin já acompanha escala, cobrança, cancelamentos e suporte por segmento.</li>
                      <li>Para produção, conecte billing e storage externos antes de ampliar volume.</li>
                      <li>No mobile, prefira usar o menu lateral recolhível para navegar entre áreas.</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        <Dialog open={cancellationAlertOpen} onOpenChange={(open) => {
          setCancellationAlertOpen(open);
          if (!open) {
            setHasSeenCancellationAlert(true);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nova solicitação financeira</DialogTitle>
              <DialogDescription>
                {stats?.billing_requests?.pending || 0} solicitação(ões) de cancelamento ou reativação aguardam o time.
              </DialogDescription>
            </DialogHeader>
            {stats?.billing_requests?.latest ? (
              <div className="rounded-xl border border-zinc-200 p-4 text-sm text-zinc-700">
                <div className="flex items-center gap-2 mb-2">
                  {getBillingRequestTypeBadge(stats.billing_requests.latest.request_type)}
                  {getBillingRequestStatusBadge(stats.billing_requests.latest.status)}
                </div>
                <p className="font-medium text-zinc-900">{stats.billing_requests.latest.user_name}</p>
                <p className="text-zinc-500">{stats.billing_requests.latest.email}</p>
                {stats.billing_requests.latest.effective_at ? (
                  <p>Acesso até {format(parseISO(stats.billing_requests.latest.effective_at), 'dd/MM/yyyy')}</p>
                ) : null}
                {stats.billing_requests.latest.admin_notes ? (
                  <p className="mt-2 text-xs text-zinc-500">{stats.billing_requests.latest.admin_notes}</p>
                ) : null}
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancellationAlertOpen(false)}>
                Fechar
              </Button>
              <Button onClick={() => {
                setActiveTab('billing-requests');
                setCancellationAlertOpen(false);
                fetchBillingRequests();
              }}>
                Abrir Kanban
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(selectedBillingRequest)} onOpenChange={(open) => {
          if (!open) {
            setSelectedBillingRequest(null);
            setBillingRequestNotes('');
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Atualizar solicitação</DialogTitle>
              <DialogDescription>
                Defina a próxima etapa do pedido e registre observações para o time.
              </DialogDescription>
            </DialogHeader>
            {selectedBillingRequest ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-zinc-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {getBillingRequestTypeBadge(selectedBillingRequest.request_type)}
                    {getBillingRequestStatusBadge(selectedBillingRequest.status)}
                  </div>
                  <p className="font-medium text-zinc-900">{selectedBillingRequest.user_name}</p>
                  <p className="text-sm text-zinc-500">{selectedBillingRequest.email}</p>
                  {selectedBillingRequest.reason ? (
                    <p className="text-sm text-zinc-700 mt-3">{selectedBillingRequest.reason}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing-request-notes">Observações internas</Label>
                  <Input
                    id="billing-request-notes"
                    value={billingRequestNotes}
                    onChange={(event) => setBillingRequestNotes(event.target.value)}
                    placeholder="Ex.: cobrança cancelada manualmente no provedor"
                  />
                </div>
              </div>
            ) : null}
            <DialogFooter className="sm:justify-between">
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => selectedBillingRequest && updateBillingRequestStatus(selectedBillingRequest.request_id, 'in_progress', billingRequestNotes)}>
                  Em tratamento
                </Button>
                <Button variant="outline" onClick={() => selectedBillingRequest && updateBillingRequestStatus(selectedBillingRequest.request_id, 'payment_cancelled', billingRequestNotes)}>
                  Pagamento cancelado
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => selectedBillingRequest && updateBillingRequestStatus(selectedBillingRequest.request_id, 'dismissed', billingRequestNotes)}>
                  Encerrar
                </Button>
                <Button onClick={() => selectedBillingRequest && updateBillingRequestStatus(selectedBillingRequest.request_id, 'completed', billingRequestNotes)}>
                  Concluir
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
    </div>
  );
}
